/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AccountData } from '../models/AccountData';
import type { CloneOrderPageInput } from '../models/CloneOrderPageInput';
import type { ClonePassOrderPageInput } from '../models/ClonePassOrderPageInput';
import type { CreateLocationInput } from '../models/CreateLocationInput';
import type { CreateOrderPageInput } from '../models/CreateOrderPageInput';
import type { CreatePassOrderPageInput } from '../models/CreatePassOrderPageInput';
import type { CreateStorefrontInput } from '../models/CreateStorefrontInput';
import type { CustomInquiryInput } from '../models/CustomInquiryInput';
import type { EmailBlockData } from '../models/EmailBlockData';
import type { EmailSettingsUpdateData } from '../models/EmailSettingsUpdateData';
import type { EmailTemplatesContent } from '../models/EmailTemplatesContent';
import type { EmailTemplatesSettingsUpdateData } from '../models/EmailTemplatesSettingsUpdateData';
import type { EmailTemplateType } from '../models/EmailTemplateType';
import type { GcalConnectDTO } from '../models/GcalConnectDTO';
import type { Language } from '../models/Language';
import type { OrderCompletedPageSettingsData } from '../models/OrderCompletedPageSettingsData';
import type { PassCustomerPortalSettingsData } from '../models/PassCustomerPortalSettingsData';
import type { SMSTemplatesSettingsUpdateData } from '../models/SMSTemplatesSettingsUpdateData';
import type { UpdateAccountGeneralInput } from '../models/UpdateAccountGeneralInput';
import type { UpdateAccountIntegrationsInput } from '../models/UpdateAccountIntegrationsInput';
import type { UpdateAccountSyncInput } from '../models/UpdateAccountSyncInput';
import type { UpdateBookingFeeData } from '../models/UpdateBookingFeeData';
import type { UpdateBusinessDetailsInput } from '../models/UpdateBusinessDetailsInput';
import type { UpdateCalendarDisplaySettingsData } from '../models/UpdateCalendarDisplaySettingsData';
import type { UpdateGuestPassSettingsInput } from '../models/UpdateGuestPassSettingsInput';
import type { UpdateLocationInput } from '../models/UpdateLocationInput';
import type { UpdateMessageShortcutsData } from '../models/UpdateMessageShortcutsData';
import type { UpdateOrderPageInput } from '../models/UpdateOrderPageInput';
import type { UpdateOtherPaymentMethodsData } from '../models/UpdateOtherPaymentMethodsData';
import type { UpdatePassOrderPageInput } from '../models/UpdatePassOrderPageInput';
import type { UpdateRefundReasonsData } from '../models/UpdateRefundReasonsData';
import type { UpdateReportPresetsData } from '../models/UpdateReportPresetsData';
import type { UpdateReschedulePolicyData } from '../models/UpdateReschedulePolicyData';
import type { UpdateSharedResourcesData } from '../models/UpdateSharedResourcesData';
import type { UpdateStorefrontInput } from '../models/UpdateStorefrontInput';
import type { WhatsAppSettingsUpdateData } from '../models/WhatsAppSettingsUpdateData';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class AccountService {
  /**
   * Get Account
   * @param accountId
   * @returns any Successful Response
   * @throws ApiError
   */
  public static getAccount(accountId: any): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/account',
      query: {
        account_id: accountId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Account Typed
   * @param accountId
   * @returns AccountData Successful Response
   * @throws ApiError
   */
  public static getAccountTyped(accountId: any): CancelablePromise<AccountData> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/account/dontuseiterrors',
      query: {
        account_id: accountId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Authenticated Account
   * @returns any Successful Response
   * @throws ApiError
   */
  public static getAccountAuthenticated(): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/account/authenticated',
    });
  }

  /**
   * Update Order Page
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateOrderPage(requestBody: UpdateOrderPageInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/order-page',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Order Page
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static createOrderPage(requestBody: CreateOrderPageInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/order-page',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Archive Order Page
   * @param orderPageId
   * @returns any Successful Response
   * @throws ApiError
   */
  public static archiveOrderPage(orderPageId: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/account/order-page',
      query: {
        order_page_id: orderPageId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Restore Order Page
   * @param orderPageId
   * @returns any Successful Response
   * @throws ApiError
   */
  public static restoreOrderPage(orderPageId: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/account/order-page',
      query: {
        order_page_id: orderPageId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Clone Order Page
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static cloneOrderPage(requestBody: CloneOrderPageInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/order-page/clone',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Clone Pass Order Page
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static clonePassOrderPage(requestBody: ClonePassOrderPageInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/pass-order-page/clone',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Pass Order Page
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static createPassOrderPage(requestBody: CreatePassOrderPageInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/pass-order-page',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Pass Order Page
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updatePassOrderPage(requestBody: UpdatePassOrderPageInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/pass-order-page',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Archive Pass Order Page
   * @param passOrderPageId
   * @returns any Successful Response
   * @throws ApiError
   */
  public static archivePassOrderPage(passOrderPageId: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/account/pass-order-page',
      query: {
        pass_order_page_id: passOrderPageId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Account Settings
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static syncSettings(requestBody: UpdateAccountSyncInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/sync-settings',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Account Settings
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static integrationsSettings(requestBody: UpdateAccountIntegrationsInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/integrations-settings',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Account Settings
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static generalSettings(requestBody: UpdateAccountGeneralInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/general-settings',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Gcal Connect
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static accountGcalConnect(requestBody: GcalConnectDTO): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/gcal-connect',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Email Templates Parts
   * @returns EmailTemplatesContent Successful Response
   * @throws ApiError
   */
  public static getEmailTemplatesParts(): CancelablePromise<EmailTemplatesContent> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/account/email-templates-parts',
    });
  }

  /**
   * Update Email Templates
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateEmailTemplate(requestBody: EmailSettingsUpdateData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/email-templates',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Email Templates
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateEmailTemplatesSettings(requestBody: EmailTemplatesSettingsUpdateData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/email-templates-settings',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Sms Templates Settings
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateSmsTemplatesSettings(requestBody: SMSTemplatesSettingsUpdateData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/sms-templates-settings',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Whatsapp Settings
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateWhatsappSettings(requestBody: WhatsAppSettingsUpdateData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/whatsapp-settings',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Email Templates Parts
   * @param orderPageId Order page id
   * @param orderPageType End date (YYYY-MM-DD)
   * @param language End date (YYYY-MM-DD)
   * @param email Email to send test to
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static sendTestEmail(
    orderPageId?: string,
    orderPageType?: EmailTemplateType,
    language?: Language,
    email?: string,
    requestBody?: Array<EmailBlockData>,
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/test-email',
      query: {
        order_page_id: orderPageId,
        order_page_type: orderPageType,
        language: language,
        email: email,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Stripe Integration
   * @param code Authorization
   * @param isTestMode Is test mode
   * @returns any Successful Response
   * @throws ApiError
   */
  public static createStripeIntegration(code?: string, isTestMode: boolean = false): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/create-stripe-integration',
      query: {
        code: code,
        is_test_mode: isTestMode,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Disconnect Stripe Integration
   * @returns any Successful Response
   * @throws ApiError
   */
  public static disconnectStripeIntegration(): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/disconnect-stripe-integration',
    });
  }

  /**
   * Create Location
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static createLocation(requestBody: CreateLocationInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/location',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Location
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateLocation(requestBody: UpdateLocationInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/location',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Archive Location
   * @param locationId
   * @returns any Successful Response
   * @throws ApiError
   */
  public static deleteLocation(locationId: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/account/location',
      query: {
        location_id: locationId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Account Marketing Integrations
   * @param accountId
   * @returns any Successful Response
   * @throws ApiError
   */
  public static getMarketingIntegrations(accountId: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/account/marketing-integrations',
      query: {
        account_id: accountId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Order Completed Page Settings
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateOrderCompletedPageSettings(requestBody: OrderCompletedPageSettingsData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/order-completed-page-settings',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Shared Resources
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static sharedResources(requestBody: UpdateSharedResourcesData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/shared-resources',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Svix Application
   * @returns any Successful Response
   * @throws ApiError
   */
  public static createSvixApplication(): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/create-svix-application',
    });
  }

  /**
   * Get Svix App Portal
   * @returns string Successful Response
   * @throws ApiError
   */
  public static getSvixAppPortal(): CancelablePromise<string> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/account/svix-app-portal',
    });
  }

  /**
   * Update Calendar Display Settings
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateCalendarDisplaySettings(requestBody: UpdateCalendarDisplaySettingsData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/calendar-display-settings',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Report Presets
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateReportPresets(requestBody: UpdateReportPresetsData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/report-presets',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Other Payment Methods
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateOtherPaymentMethods(requestBody: UpdateOtherPaymentMethodsData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/other-payment-methods',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Refund Reasons
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateRefundReasons(requestBody: UpdateRefundReasonsData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/refund-reasons',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Booking Fee
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateBookingFee(requestBody: UpdateBookingFeeData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/booking-fee',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Message Shortcuts
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateMessageShortcuts(requestBody: UpdateMessageShortcutsData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/message-shortcuts',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Reschedule Policy
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateReschedulePolicy(requestBody: UpdateReschedulePolicyData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/reschedule-policy',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Storefront
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static createStorefront(requestBody: CreateStorefrontInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/storefront',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Storefront
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateStorefront(requestBody: UpdateStorefrontInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/storefront',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Delete Storefront
   * @param storefrontId
   * @returns any Successful Response
   * @throws ApiError
   */
  public static deleteStorefront(storefrontId: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/account/storefront',
      query: {
        storefront_id: storefrontId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Restore Storefront
   * @param storefrontId
   * @returns any Successful Response
   * @throws ApiError
   */
  public static restoreStorefront(storefrontId: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/account/storefront',
      query: {
        storefront_id: storefrontId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Business Details
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateBusinessDetails(requestBody: UpdateBusinessDetailsInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/business-details',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Guest Pass Settings
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updateGuestPassSettings(requestBody: UpdateGuestPassSettingsInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/guest-pass-settings',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Pass Customer Portal Settings
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static updatePassCustomerPortalSettings(requestBody: PassCustomerPortalSettingsData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/account/pass-customer-portal-settings',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Submit Custom Inquiry
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static submitCustomInquiry(requestBody: CustomInquiryInput): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/account/storefront/custom-inquiry',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }
}
